import { useAtomValue, useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useUpdateDocumentImageMutation } from '~/components/features/DocumentImageInfiniteScrollList/hooks/useUpdateDocumentImageMutation'
import {
  useBrandAccountAiEnabledQuery,
  useInvalidateBrandAccountAiEnabledQuery,
} from '~/hooks/useBrandAccountAiEnabledQuery'
import { useCommonQueryCurrentBrandAccount } from '~/hooks/useCommonQuery'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery/useSurveyQueryData'
import { useSurveyQueryQuestions } from '~/hooks/useSurveyQuery/useSurveyQueryQuestions'
import { ensureExists, exists, isAiDisabledError } from '~/utils'

import { phaseAtom } from '../atoms'

import { useBasicOutputWizard } from './useBasicOutputWizard'
import {
  type CommentsMap,
  useGenerateAnalysisComments,
} from './useGenerateAnalysisComments'
import {
  type BasicOutputsResult,
  useGenerateBasicOutputs,
} from './useGenerateBasicOutputs'
import { useGenerateDocuments } from './useGenerateDocuments'
import {
  type SavedImagesMap,
  useRenderAndSaveDocumentImages,
} from './useRenderAndSaveDocumentImages'

export const useBasicOutputGenerationFlow = ({
  surveyId,
}: {
  surveyId: string
}) => {
  // Per-execution AI toggle. Independent from BrandAccount.aiEnabled — both
  // must be ON for AI to execute (dual-toggle semantic). When OFF, the
  // commenting phase is skipped; no LLM call.
  const [aiOn, setAiOn] = React.useState(true)
  const { t } = useTranslation()
  const wizard = useBasicOutputWizard()
  const [asyncState, startFn, pollForResult] = useGenerateBasicOutputs()
  // Surfaces a failure from any POST-submit phase (tabulation poll, rendering,
  // commenting, documenting). Those phases reset `phase` to 'input' on failure;
  // without a recorded error the wizard's recovery effect would bounce the user
  // back to the confirmation step with no message. Routing this into submitError
  // keeps them on the progress step with the error shown (same surface as an
  // initial-submit failure). Reset per run in startGeneration; each setter site
  // is behind the survey epoch guard so a stale survey's failure never leaks.
  const [phaseError, setPhaseError] = React.useState<Error | undefined>(
    undefined,
  )
  const resultRef = React.useRef<BasicOutputsResult | null>(null)
  const { progress: renderProgress, start: startRendering } =
    useRenderAndSaveDocumentImages()
  const { progress: documentProgress, start: startDocuments } =
    useGenerateDocuments()
  const { progress: commentProgress, start: startComments } =
    useGenerateAnalysisComments()
  const updateDocumentImage = useUpdateDocumentImageMutation()
  const currentBrandAccount = useCommonQueryCurrentBrandAccount()
  const questions = useSurveyQueryQuestions()
  const survey = useSurveyQueryData()
  const phase = useAtomValue(phaseAtom)
  const setPhase = useSetAtom(phaseAtom)
  const pollingStartedRef = React.useRef(false)
  const renderingStartedRef = React.useRef(false)
  const commentingStartedRef = React.useRef(false)
  const documentingStartedRef = React.useRef(false)
  const savedImagesRef = React.useRef<SavedImagesMap>(new Map())
  const commentsMapRef = React.useRef<CommentsMap>(new Map())
  // Survey-scoped epoch guard. The component instance is reused across
  // :surveyId navigations (React Router), so an in-flight poll/render promise
  // started for survey A can resolve AFTER the user switched to survey B and
  // then call setPhase / write refs — leaking A's artifacts into B. We bump the
  // epoch whenever surveyId changes; every async callback captures the epoch at
  // start and abandons (no setPhase, no ref write) if it no longer matches.
  const epochRef = React.useRef(0)
  React.useEffect(() => {
    epochRef.current += 1
  }, [surveyId])
  // The surveyId a submit was started for; used to scope isSubmitting/submitError
  // so a prior survey's in-flight/errored submit does not leak onto a new survey.
  const submitSurveyIdRef = React.useRef<string | null>(null)
  const invalidateBrandAccountAiEnabledQuery =
    useInvalidateBrandAccountAiEnabledQuery(currentBrandAccount?.id)
  const aiEnabledQuery = useBrandAccountAiEnabledQuery(currentBrandAccount?.id)
  const aiEnabledData = aiEnabledQuery.data
  // Only a SUCCESSFUL AI-state query resolves the state. A query ERROR is an
  // internal-API failure: we cannot confirm the tenant AI policy, so we surface
  // the error (aiEnabledError) with a retry and block generation, rather than
  // silently proceeding with AI off. On success but no brandAccount, default AI
  // OFF (skip the LLM comment phase) rather than risk an AI_DISABLED rejection.
  const isAiStateResolved = aiEnabledQuery.isSuccess
  const aiEnabledError = aiEnabledQuery.isError ? aiEnabledQuery.error : null
  const brandAccountAiEnabled = aiEnabledData?.brandAccount?.aiEnabled ?? false
  const retryAiEnabled = React.useCallback(() => {
    void aiEnabledQuery.refetch()
  }, [aiEnabledQuery])
  // Effective execution requires BOTH flags. Captured in a ref so the post-
  // rendering effect reads a stable value at phase-transition time.
  const effectiveAiOn = aiOn && brandAccountAiEnabled
  const effectiveAiOnRef = React.useRef(effectiveAiOn)
  effectiveAiOnRef.current = effectiveAiOn

  const startGeneration = React.useCallback(async () => {
    // Scope the submit lifecycle (isSubmitting / submitError) to the survey it
    // was started for. The instance is reused across :surveyId navigations, so
    // an in-flight or errored submit from survey A must not surface as loading
    // or an error on survey B (asyncState has no reset).
    submitSurveyIdRef.current = surveyId
    // Reset per-run state so a re-run in the same mount starts fresh.
    setPhaseError(undefined)
    pollingStartedRef.current = false
    renderingStartedRef.current = false
    commentingStartedRef.current = false
    documentingStartedRef.current = false
    resultRef.current = null
    savedImagesRef.current = new Map()
    commentsMapRef.current = new Map()

    // Guard against an unresolved AI state. ConfirmationStep's submit button is
    // disabled until isAiStateResolved (BasicOutputWizard passes
    // isSubmitting={... || !flow.isAiStateResolved}), so the UI normally never
    // invokes startGeneration while unresolved. But a failed AI-state query
    // (internal-API error) keeps isAiStateResolved false until the user retries,
    // so this guard intentionally blocks generation in that case too (FATAL).
    if (!isAiStateResolved) {
      return undefined
    }

    const brandGroupId = ensureExists(
      wizard.brandGroupId,
      'brandGroupId must be set before submit',
    )

    // Brand selection is explicit and required: the BrandSelectionStep "Next"
    // button is disabled until isBrandSelectionValid (selectedBrandIds.length > 0
    // || isEntireBrandGroupSelected), so generation cannot start with neither set.
    // Assert that contract here — the single generation entry point — and pass the
    // selection through explicitly. There is no "empty means all brands" convention.
    if (
      wizard.selectedBrandIds.length === 0 &&
      !wizard.isEntireBrandGroupSelected
    ) {
      throw new Error(
        'selectedBrandIds and isEntireBrandGroupSelected must not both be empty before generation',
      )
    }

    // Fence the initial submit against survey changes. startFn's response sets
    // the shared phaseAtom/awaitIdAtom; if the user navigates A→B while it is in
    // flight, A's response would otherwise flip phase/awaitId for the reused
    // instance before the later effects' epoch guard can run. Capture the epoch
    // at the start and abandon if it no longer matches after the await.
    const epoch = epochRef.current
    const awaitId = await startFn({
      brandGroupId,
      brandIds: wizard.selectedBrandIds,
      customerAttributeGroups: survey?.customerAttributeGroups ?? [],
      customRoleAxes: wizard.customRoleAxes,
      filterPatternDrafts: wizard.filterPatternDrafts,
      isEntireBrandGroupSelected: wizard.isEntireBrandGroupSelected,
      questionAssignments: wizard.questionAssignments,
      questions,
      surveyId,
    })

    if (epoch !== epochRef.current) {
      return undefined
    }

    if (!exists(awaitId)) {
      return undefined
    }

    return awaitId
  }, [
    isAiStateResolved,
    questions,
    startFn,
    survey?.customerAttributeGroups,
    surveyId,
    wizard.brandGroupId,
    wizard.customRoleAxes,
    wizard.filterPatternDrafts,
    wizard.isEntireBrandGroupSelected,
    wizard.questionAssignments,
    wizard.selectedBrandIds,
  ])

  // Poll for tabulation result when phase becomes 'tabulating'
  React.useEffect(() => {
    if (
      phase !== 'tabulating' ||
      pollingStartedRef.current ||
      !exists(asyncState.value)
    ) {
      return
    }

    pollingStartedRef.current = true
    const epoch = epochRef.current
    void pollForResult(asyncState.value).then((outcome) => {
      // Abandon stale results from a survey the user already navigated away from.
      if (epoch !== epochRef.current) {
        return
      }
      if (outcome.kind === 'serverError' || outcome.kind === 'emptyResult') {
        // Surface a cause-specific failure on the progress step instead of a
        // silent bounce to step 4.
        setPhaseError(
          new Error(
            t(
              outcome.kind === 'serverError'
                ? 'basic_output_tabulation_failed'
                : 'basic_output_tabulation_empty',
            ),
          ),
        )
        setPhase('input')

        return
      }
      if (outcome.kind === 'aborted') {
        // Component unmounted mid-poll; do not touch state.
        return
      }
      // Write the ref BEFORE the phase flips to 'rendering' so the rendering
      // effect (gated on phase==='rendering' && exists(resultRef.current))
      // observes a populated ref. pollForResult intentionally no longer sets
      // the phase itself, to keep this ordering race-free.
      resultRef.current = outcome.result
      setPhase('rendering')
    })
  }, [asyncState.value, phase, pollForResult, setPhase, t])

  React.useEffect(() => {
    if (
      phase !== 'rendering' ||
      renderingStartedRef.current ||
      !exists(resultRef.current) ||
      !exists(currentBrandAccount)
    ) {
      return
    }

    renderingStartedRef.current = true
    const epoch = epochRef.current
    const brandGroupId = ensureExists(
      wizard.brandGroupId,
      'brandGroupId must be set before rendering',
    )
    const fullBrands =
      survey?.brandGroups.find((bg) => bg.id === brandGroupId)?.brands ?? []
    const brandIdToOriginalIndex = new Map(fullBrands.map((b, i) => [b.id, i]))

    void startRendering({
      // Only mark saved figures with a commentRef when this run generates comments;
      // the render-save hook attaches the marker iff this is true.
      aiCommentsEnabled: effectiveAiOnRef.current,
      brandAccountId: currentBrandAccount.id,
      brandGroupId,
      brandIdToOriginalIndex,
      customerAttributeGroups: survey?.customerAttributeGroups ?? [],
      customLabels: wizard.customLabels,
      customRoleAxes: wizard.customRoleAxes,
      filterPatternFilterGroups: wizard.filterPatternDrafts.map(
        (draft) => draft.filterGroups,
      ),
      getBrandAligned: wizard.getBrandAligned,
      isEntireBrandGroupSelected: wizard.isEntireBrandGroupSelected,
      ownBrandIds: wizard.ownBrandIds,
      questionAssignments: wizard.questionAssignments,
      questions,
      result: resultRef.current,
      selectedBrandIds: wizard.selectedBrandIds,
      surveyId,
    })
      .then((savedImages) => {
        if (epoch !== epochRef.current) {
          return
        }
        savedImagesRef.current = savedImages
        // AI off: skip the commenting phase entirely. The documents will be
        // assembled without an analysis comment block (useGenerateDocuments
        // accepts an undefined commentsMap).
        setPhase(effectiveAiOnRef.current ? 'commenting' : 'documenting')
      })
      .catch((error) => {
        if (epoch !== epochRef.current) {
          return
        }
        console.error('Rendering document images failed:', error)
        setPhaseError(error instanceof Error ? error : new Error(String(error)))
        setPhase('input')
      })
  }, [
    currentBrandAccount,
    phase,
    questions,
    setPhase,
    startRendering,
    survey?.brandGroups,
    survey?.customerAttributeGroups,
    surveyId,
    wizard.brandGroupId,
    wizard.customLabels,
    wizard.customRoleAxes,
    wizard.filterPatternDrafts,
    wizard.getBrandAligned,
    wizard.isEntireBrandGroupSelected,
    wizard.ownBrandIds,
    wizard.questionAssignments,
    wizard.selectedBrandIds,
  ])

  React.useEffect(() => {
    if (
      phase !== 'commenting' ||
      commentingStartedRef.current ||
      !exists(resultRef.current)
    ) {
      return
    }

    commentingStartedRef.current = true
    const epoch = epochRef.current
    const fullBrandsForComments =
      survey?.brandGroups.find((bg) => bg.id === wizard.brandGroupId)?.brands ??
      []
    const brandIdToOriginalIndexForComments = new Map(
      fullBrandsForComments.map((b, i) => [b.id, i]),
    )
    void startComments({
      aiCommentsEnabled: effectiveAiOnRef.current,
      brandGroupId: ensureExists(
        wizard.brandGroupId,
        'brandGroupId must be set before commenting',
      ),
      brandIdToOriginalIndex: brandIdToOriginalIndexForComments,
      brandRoleContext: {
        brands: fullBrandsForComments,
        keyCompetitorBrandIds: wizard.keyCompetitorBrandIds,
        ownBrandIds: wizard.ownBrandIds,
      },
      customerAttributeGroups: survey?.customerAttributeGroups ?? [],
      customRoleAxes: wizard.customRoleAxes,
      filterPatternFilterGroups: wizard.filterPatternDrafts.map(
        (draft) => draft.filterGroups,
      ),
      getBrandAligned: wizard.getBrandAligned,
      isEntireBrandGroupSelected: wizard.isEntireBrandGroupSelected,
      questionAssignments: wizard.questionAssignments,
      questions,
      result: resultRef.current,
      selectedBrandIds: wizard.selectedBrandIds,
      surveyId,
    })
      .then(async (commentsMap) => {
        if (epoch !== epochRef.current) {
          return
        }
        commentsMapRef.current = commentsMap
        // Associate generated comments with saved DocumentImages
        const savedImagesMap = savedImagesRef.current
        const updatePromises: Promise<unknown>[] = []

        for (const [, entries] of savedImagesMap) {
          for (const entry of entries) {
            // Direct lookup by the unique outputKey carried on each saved image.
            const comment = commentsMap.get(entry.outputKey)
            if (!exists(comment)) {
              continue
            }

            // updateDocumentImage is an INTERNAL API: do NOT swallow its
            // rejection. Let it surface as a rejected Promise.allSettled result
            // so the run fails fast below rather than silently dropping a
            // comment association.
            updatePromises.push(
              updateDocumentImage.mutateAsync({
                comment,
                documentImageId: entry.id,
                title: entry.title,
              }),
            )
          }
        }

        const updateResults = await Promise.allSettled(updatePromises)
        // Re-check after the awaits: the survey may have switched DURING the
        // comment-association mutations. Abandon so a stale continuation does
        // not invalidate queries or flip phase for the new survey.
        if (epoch !== epochRef.current) {
          return
        }
        // Any rejected association is an internal-API failure: fail fast.
        const rejectedUpdates = updateResults.filter(
          (r) => r.status === 'rejected',
        )
        if (0 < rejectedUpdates.length) {
          console.error(
            '[BOG] Comment association failed for',
            rejectedUpdates.length,
            'image(s):',
            rejectedUpdates,
          )
          const firstReason = (rejectedUpdates[0] as PromiseRejectedResult)
            .reason
          setPhaseError(
            firstReason instanceof Error
              ? firstReason
              : new Error('comment association failed'),
          )
          setPhase('input')

          return
        }
        // Refetch the BrandAccount aiEnabled status after generation so the UI
        // reflects any side-effects (e.g., backend toggling aiEnabled) immediately.
        void invalidateBrandAccountAiEnabledQuery()
        setPhase('documenting')
      })
      .catch((error) => {
        if (epoch !== epochRef.current) {
          return
        }
        console.error('Generating analysis comments failed:', error)
        if (isAiDisabledError(error)) {
          // Reaching the server gate means the cached aiEnabled flag still said
          // "on". Drop it so the confirmation step reflects the real state.
          void invalidateBrandAccountAiEnabledQuery()
        }
        setPhaseError(error instanceof Error ? error : new Error(String(error)))
        setPhase('input')
      })
  }, [
    currentBrandAccount?.id,
    invalidateBrandAccountAiEnabledQuery,
    phase,
    questions,
    setPhase,
    startComments,
    survey?.brandGroups,
    survey?.customerAttributeGroups,
    surveyId,
    updateDocumentImage,
    wizard.brandGroupId,
    wizard.customRoleAxes,
    wizard.filterPatternDrafts,
    wizard.getBrandAligned,
    wizard.isEntireBrandGroupSelected,
    wizard.keyCompetitorBrandIds,
    wizard.ownBrandIds,
    wizard.questionAssignments,
    wizard.selectedBrandIds,
  ])

  React.useEffect(() => {
    if (
      phase !== 'documenting' ||
      documentingStartedRef.current ||
      !exists(resultRef.current) ||
      !exists(currentBrandAccount)
    ) {
      return
    }

    documentingStartedRef.current = true
    const epoch = epochRef.current
    const surveyTitle = survey?.title ?? ''
    const brandGroupName =
      survey?.brandGroups.find(({ id }) => id === wizard.brandGroupId)?.name ??
      ''

    void startDocuments({
      brandAccountId: currentBrandAccount.id,
      brandGroupName,
      commentsMap: commentsMapRef.current,
      customLabels: wizard.customLabels,
      questionAssignments: wizard.questionAssignments,
      result: resultRef.current,
      savedImagesMap: savedImagesRef.current,
      surveyTitle,
    })
      .then(() => {
        if (epoch !== epochRef.current) {
          return
        }
        setPhase('complete')
      })
      .catch((error) => {
        if (epoch !== epochRef.current) {
          return
        }
        console.error('Generating documents failed:', error)
        setPhaseError(error instanceof Error ? error : new Error(String(error)))
        setPhase('input')
      })
  }, [
    currentBrandAccount,
    phase,
    setPhase,
    startDocuments,
    survey?.brandGroups,
    survey?.title,
    wizard.brandGroupId,
    wizard.customLabels,
    wizard.questionAssignments,
  ])

  // Only report the submit lifecycle for the CURRENT survey (see
  // submitSurveyIdRef): a prior survey's loading/error must not leak across a
  // :surveyId navigation that reuses this instance.
  const isSubmitForCurrentSurvey = submitSurveyIdRef.current === surveyId

  return {
    aiEnabled: brandAccountAiEnabled,
    aiEnabledError,
    brandAccountId: currentBrandAccount?.id,
    commentProgress,
    documentProgress,
    effectiveAiOn,
    isAiStateResolved,
    isSubmitting: asyncState.loading && isSubmitForCurrentSurvey,
    phase,
    renderProgress,
    retryAiEnabled,
    setAiOn,
    startGeneration,
    // phaseError (post-submit failures) takes precedence over the initial-submit
    // asyncState.error; either keeps the user on the progress step with a message
    // instead of the recovery effect silently bouncing back to confirmation.
    submitError: isSubmitForCurrentSurvey
      ? (phaseError ?? asyncState.error)
      : undefined,
  }
}
